import {
	AdminActionArea,
	AdminActionGroup,
	AdminActionKind,
	type GetAdminActionQueueQuery,
} from '@codezo/graphql';

export type ActionItem = GetAdminActionQueueQuery['getAdminActionQueue']['items'][number];

/** Where an item's action button goes. Quote work opens the quote; everything else the order. */
export function actionHref(item: ActionItem): string {
	const quoteKinds: AdminActionKind[] = [
		AdminActionKind.LateAcceptance,
		AdminActionKind.OfferExpired,
		AdminActionKind.OfferRequested,
		AdminActionKind.SupplierQuestion,
		AdminActionKind.OfferReadyToSend,
		AdminActionKind.SupplierPricesIn,
		AdminActionKind.OfferDeclined,
		AdminActionKind.OfferAwaitingAnswer,
	];
	if (quoteKinds.includes(item.kind) && item.orderQuoteId) return `/quotes/${item.orderQuoteId}`;
	if (item.orderId) return `/orders/${item.orderId}`;
	if (item.orderQuoteId) return `/quotes/${item.orderQuoteId}`;
	return '/dashboard';
}

/** The reference shown beside the client: the order number, else the quote's name. */
export function itemRef(item: ActionItem): string | null {
	// Short legacy numbers read as "#2418"; generated ones (QO-…) are shown as they are.
	if (item.orderNumber)
		return /^\d+$/.test(item.orderNumber) ? `#${item.orderNumber}` : item.orderNumber;
	return item.quoteName ?? null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const calendarDaysBetween = (from: Date, to: Date) =>
	Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);

const shortDate = (d: Date) =>
	d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

export type Tone = 'danger' | 'warn' | 'muted';

/**
 * A past moment in the dashboard's voice: "3 h ago" today, "yesterday", "Mon 19" this past week,
 * "16 Oct" before that.
 */
export function relativeDay(d: Date, now = new Date()): string {
	const days = calendarDaysBetween(d, now);
	if (days <= 0) {
		const hours = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 3_600_000));
		return hours < 1 ? 'just now' : `${hours} h ago`;
	}
	if (days === 1) return 'yesterday';
	if (days < 7) return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
	return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).replace('Sept', 'Sep');
}

/**
 * What the item's waitingSince means, per kind — each collector stamps a specific event. Null kinds
 * read the date on its own ("3 h ago"). Client deadline at risk has no event of its own; its date is
 * the deadline in the due column.
 */
const SINCE_VERB: Partial<Record<AdminActionKind, string>> = {
	[AdminActionKind.ProductionLate]: 'Started',
	[AdminActionKind.ShipmentOverdue]: 'Shipped',
	[AdminActionKind.LateAcceptance]: 'Accepted',
	[AdminActionKind.OfferExpired]: 'Sent',
	[AdminActionKind.ReadyToShip]: 'Ready since',
	[AdminActionKind.OfferRequested]: 'Requested',
	[AdminActionKind.SupplierQuestion]: 'Asked',
	[AdminActionKind.OfferReadyToSend]: 'Drafted',
	[AdminActionKind.SupplierPricesIn]: 'Priced',
	[AdminActionKind.OfferDeclined]: 'Declined',
	[AdminActionKind.ConfirmProducts]: 'Accepted',
	[AdminActionKind.StartSampleProduction]: 'Since',
	[AdminActionKind.ShipSample]: 'Sample done',
	[AdminActionKind.ConfirmSampleDelivery]: 'Shipped',
	[AdminActionKind.NewSampleRound]: 'Rejected',
	[AdminActionKind.StartProduction]: 'Since',
};

/**
 * The row's "since" column. Waiting-on-client items show how long the client has had it ("8 days");
 * everything else names what happened and when ("Shipped 16 Oct", "Ready since Mon 19").
 */
export function sinceLabel(item: ActionItem, now = new Date()): { text: string; tone: Tone } {
	if (!item.waitingSince) return { text: '—', tone: 'muted' };
	const at = new Date(item.waitingSince);
	if (item.group === AdminActionGroup.WaitingOnClient) {
		const days = calendarDaysBetween(at, now);
		return {
			text: days <= 0 ? 'Today' : plural(days, 'day'),
			tone: days >= 7 ? 'danger' : days >= 3 ? 'warn' : 'muted',
		};
	}
	const verb = SINCE_VERB[item.kind];
	const when = relativeDay(at, now);
	if (!verb) return { text: when.charAt(0).toUpperCase() + when.slice(1), tone: 'muted' };
	return { text: `${verb} ${when}`, tone: 'muted' };
}

/**
 * The right-hand date: how late, or when it is due. Blank without a due date — the since column
 * already says how long it has been waiting.
 */
export function dueLabel(item: ActionItem, now = new Date()): { text: string; tone: Tone } {
	if (item.businessDaysLate > 0) {
		return { text: `${plural(item.businessDaysLate, 'day')} late`, tone: 'danger' };
	}
	if (item.dueAt) {
		const due = new Date(item.dueAt);
		const days = calendarDaysBetween(now, due);
		if (days < 0) return { text: `${plural(-days, 'day')} late`, tone: 'danger' };
		if (days === 0) return { text: 'Today', tone: 'warn' };
		if (days === 1) return { text: 'Tomorrow', tone: 'warn' };
		return { text: shortDate(due), tone: 'muted' };
	}
	return { text: '—', tone: 'muted' };
}

export const AREA_LABELS: Record<AdminActionArea, string> = {
	[AdminActionArea.Quote]: 'Quotes & offers',
	[AdminActionArea.Production]: 'Production',
	[AdminActionArea.Sample]: 'Samples',
	[AdminActionArea.Shipping]: 'Shipping',
};

export const GROUP_LABELS: Record<AdminActionGroup, string> = {
	[AdminActionGroup.OverdueOrAtRisk]: 'Overdue or at risk',
	[AdminActionGroup.StudioAction]: 'Needs studio action',
	[AdminActionGroup.WaitingOnClient]: 'Waiting on client',
};

export const GROUP_ORDER: AdminActionGroup[] = [
	AdminActionGroup.OverdueOrAtRisk,
	AdminActionGroup.StudioAction,
	AdminActionGroup.WaitingOnClient,
];

export function ownerName(owner: { firstName: string; lastName: string }): string {
	return `${owner.firstName} ${owner.lastName}`.trim();
}
